import React, { useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { T } from "../../constants";
import { useUi, cx } from "../../ui";
import Overlay from "../../components/Overlay";
import Plate from "../../components/Plate";
import DialogRow from "./DialogRow";
import { fmtDate } from "../../../../lib/misc";

/* ---------- Validate arrival (redeem or flag leakage) ---------- */

const useStyles = makeStyles({
  dialog: { width: "100%", maxWidth: 440, borderRadius: 12, border: `1px solid ${T.border}`, background: T.elevated, padding: 24, boxShadow: "0 20px 50px rgba(0,0,0,0.25)" },
  head: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 },
  eyebrow: { fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: T.subtleFg },
  heading: { marginTop: 2, fontSize: 18, fontWeight: 600, color: T.fg },
  closeBtn: { border: "none", background: "transparent", fontSize: 14, color: T.mutedFg, cursor: "pointer", fontFamily: "inherit" },
  rows: { marginTop: 16 },
  note: { marginTop: 12, borderRadius: 6, background: T.surface, padding: "8px 12px", fontSize: 12, color: T.mutedFg },
  label: { display: "block", marginTop: 16, fontSize: 11, textTransform: "uppercase", letterSpacing: 0.5, color: T.subtleFg },
  textarea: {
    width: "100%",
    minHeight: 84,
    marginTop: 6,
    boxSizing: "border-box",
    resize: "vertical",
  },
  hint: { marginTop: 6, fontSize: 11, color: T.mutedFg },
  footer: { marginTop: 24, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 },
  flagBtn: {
    borderRadius: 6,
    border: `1px solid ${T.warningSolid}`,
    background: T.warningTint,
    color: T.warningFg,
    fontWeight: 600,
    fontSize: 14,
    padding: "10px 20px",
    cursor: "pointer",
    fontFamily: "inherit",
    "&:disabled": {
      opacity: 0.4,
      cursor: "not-allowed",
      background: T.surface,
      borderColor: T.border,
      color: T.subtleFg,
    },
  },
});

export default function ValidateDialog({ booking, lot, loading, onClose, onRedeem, onFlag, installation }) {
  const s = useStyles();
  const ui = useUi();
  const [notes, setNotes] = useState(booking.flagNote || "");
  const issuedByName =
    booking.issuedByName ||
    booking.issuedBy?.name ||
    booking.issuedBy?.email ||
    null;
  const issuedAt = booking.issuedAt;
  const issuedLabel = issuedAt
    ? `${fmtDate(issuedAt, installation.timeZoneName, "ccc d LLL")} by ${issuedByName || "-"}`
    : issuedByName || "-";
  const cleanNotes = notes.trim();

  return (
    <Overlay onClose={onClose}>
      <div className={s.dialog}>
        <div className={s.head}>
          <div>
            <div className={s.eyebrow}>Validate arrival</div>
            <div className={s.heading}>{booking.voucherId ?? booking.ref}</div>
          </div>
          <button onClick={onClose} className={s.closeBtn}>x</button>
        </div>

        <div className={s.rows}>
          <DialogRow label="Guest">{booking.guestName}</DialogRow>
          <DialogRow label="Email">{booking.email ?? "-"}</DialogRow>
          <DialogRow label="Plate">{booking.plate ? <Plate size={12}>{booking.plate}</Plate> : "to follow"}</DialogRow>
          <DialogRow label="Lot">{lot?.name ?? booking.lotId ?? "-"}</DialogRow>
          <DialogRow label="Stay">{`${fmtDate(booking.checkIn, installation.timeZoneName, "ccc d LLL")} → ${fmtDate(booking.checkOut, installation.timeZoneName, "ccc d LLL")}`}</DialogRow>
          <DialogRow label="Issued">{issuedLabel}</DialogRow>
        </div>

        {booking.flagNote && <div className={s.note}>{booking.flagNote}</div>}

        <label className={s.label}>Notes / flag reason</label>
        <textarea
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          className={cx(ui.field, s.textarea)}
          placeholder="Add details for the parking team..."
        />
        <div className={s.hint}>Required when flagging leakage. Optional when marking redeemed.</div>

        <div className={s.footer}>
          <button disabled={loading || !cleanNotes} onClick={() => onFlag(cleanNotes)} className={s.flagBtn}>
            Flag leakage
          </button>
          <button disabled={loading} onClick={() => onRedeem(cleanNotes)} className={cx(ui.primaryBtn)}>
            Mark redeemed
          </button>
        </div>
      </div>
    </Overlay>
  );
}
